import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SearchFormBase } from '../../../../../components/common/search-form-base/search-form-base';
import { AppServiceHelperService } from '../../../../../services/app-service-helper.service';
import { EventBusService } from '../../../../../services/event-bus.service';

/**
 * Leases search component
 */
@Component({
  selector: 'comparables-lease-search',
  templateUrl: './comparables-lease-search.component.html',
  styleUrls: ['./comparables-lease-search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ComparablesLeaseSearchComponent extends SearchFormBase<any> implements OnInit {

  /**
   * Selected item
   */
  @Input() selectedItem: any;

  /**
   * Selected item type
   */
  @Input() selectedItemType: any = null;

  /**
   * Search event name (event bus)
   */
  protected get searchEventName(): string {
    return 'comparables.leases.search';
  }

  /**
   * Show all event name (event bus)
   */
  protected get showAllEventName(): string {
    return 'comparables.leases.showAll';
  }

  /**
   *
   * Gets the filter object
   * @param form
   * @returns
   */
  protected getFilterObject(form: UntypedFormGroup) {
    const filter = {
      logic: "and",
      filters: [],
      serverFilters: []
    };

    this.addPropertyIdFilter(filter);
    this.addSurfaceTotalFilter(form, filter);
    this.addAnnualRentFilter(form, filter);
    this.addBuildingYearFilter(form, filter);
    this.addEnergyPerformanceLabelFilter(form, filter);

    this.addFilterNoStorage(filter, form, 'tenant', 'Tenant', 'contains');
    this.addFilterNoStorage(filter, form, 'municipality', 'City', 'contains');
    this.addFilterNoStorage(filter, form, 'postcode', 'ZipCode6', 'contains');
    this.addFilterNoStorage(filter, form, 'address', 'StreetName', 'contains');
    this.addFilterNoStorage(filter, form, 'primaryUse', 'PrimaryUse', 'contains');
    this.addFilterNoStorage(filter, form, 'sector', 'Sector', 'contains');

    return filter;
  }
  /**
   * Adds energy performance filters
   * @param form
   * @param filter
   */

  private addEnergyPerformanceLabelFilter(form: UntypedFormGroup, filter: { logic: string; filters: any[]; serverFilters: any[]; }) {
    const energyPerformanceLabel = this.reflistData.ref_lease_transaction_building_energy.filter((element) => {
      return form.get('energyPerformanceLabelId').value == element.oid;
    });
    if (energyPerformanceLabel.length > 0 && energyPerformanceLabel[0].oid != null && form.get('energyPerformanceLabelId').value != '') {
      filter.filters.push({ field: 'EnergyLabelDescription', value: energyPerformanceLabel[0].description, operator: "eq" });
    }
  }

  /**
   * Add building year filter
   * @param form
   * @param filter
   */
  private addBuildingYearFilter(form: UntypedFormGroup, filter: { logic: string; filters: any[]; serverFilters: any[]; }) {
    if ((form.get('yearOfConstructionFrom').value != 0 && form.get('yearOfConstructionFrom').value != null) || (form.get('yearOfConstructionTo').value != 0 && form.get('yearOfConstructionTo').value != null)) {
      filter.filters.push({ field: 'BuildingYear', value: form.get('yearOfConstructionFrom').value + '-' + form.get('yearOfConstructionTo').value, serverValue: "{ from: " + (form.get('yearOfConstructionFrom').value || "null") + ", to: " + (form.get('yearOfConstructionTo').value || "null") + " }", operator: "contains" });
    }
  }

  /**
   * Add annual rent filter
   * @param form
   * @param filter
   */
  private addAnnualRentFilter(form: UntypedFormGroup, filter: { logic: string; filters: any[]; serverFilters: any[]; }) {
    if ((form.get('rentFrom').value != 0 && form.get('rentFrom').value != null) || (form.get('rentTo').value != 0 && form.get('rentTo').value != null)) {
      filter.filters.push({ field: 'AnnualRent', value: form.get('rentFrom').value + '-' + form.get('rentTo').value, serverValue: "{ from: " + (form.get('rentFrom').value || "null") + ", to: " + (form.get('rentTo').value || "null") + " }", operator: "contains" });
    }
  }

  /**
   * Add total surface filter
   * @param form
   * @param filter
   */
  private addSurfaceTotalFilter(form: UntypedFormGroup, filter: { logic: string; filters: any[]; serverFilters: any[]; }) {
    if ((form.get('surfaceTotalFrom').value != '' && form.get('surfaceTotalFrom').value != null) || (form.get('surfaceTotalTo').value != '' && form.get('surfaceTotalTo').value != null)) {
      filter.filters.push({ field: 'SurfaceTotal', operator: "contains", value: form.get('surfaceTotalFrom').value + '-' + form.get('surfaceTotalTo').value, serverValue: "{ from: " + (form.get('surfaceTotalFrom').value || "null") + ", to: " + (form.get('surfaceTotalTo').value || "null") + " }" });
    }
  }

  /**
   * Add property filter
   * @param filter
   */
  private addPropertyIdFilter(filter: { logic: string; filters: any[]; serverFilters: any[]; }) {
    if (this.selectedItemType === 'comparablesProperty') {
      filter.filters.push({ field: 'PropertyId', operator: 'eq', value: this.selectedItem.Oid });
    }
  }

  /**
   * Creates the FormGroup
   * @param data
   */
  protected createFormGroup(data: any): UntypedFormGroup {
    return new UntypedFormGroup({
      tenant: new UntypedFormControl(data.tenant),
      surfaceTotalFrom: new UntypedFormControl(data.surfaceTotalFrom),
      surfaceTotalTo: new UntypedFormControl(data.surfaceTotalTo),
      rentFrom: new UntypedFormControl(data.rentFrom),
      rentTo: new UntypedFormControl(data.rentTo),
      municipality: new UntypedFormControl(data.municipality),
      postcode: new UntypedFormControl(data.postcode),
      address: new UntypedFormControl(data.address),
      yearOfConstructionFrom: new UntypedFormControl(data.yearOfConstructionFrom),
      yearOfConstructionTo: new UntypedFormControl(data.yearOfConstructionTo),
      energyPerformanceLabelId: new UntypedFormControl(data.energyPerformanceLabelId),
      primaryUse: new UntypedFormControl(data.primaryUse),
      sector: new UntypedFormControl(data.sector),
    });
  }

  /**
   * Resets all fields
   */
  protected reset(): void {
    this.formData.controls['tenant'].setValue('');
    this.formData.controls['surfaceTotalFrom'].setValue('');
    this.formData.controls['surfaceTotalTo'].setValue('');
    this.formData.controls['rentFrom'].setValue('');
    this.formData.controls['rentTo'].setValue('');
    this.formData.controls['municipality'].setValue('');
    this.formData.controls['postcode'].setValue('');
    this.formData.controls['address'].setValue('');
    this.formData.controls['yearOfConstructionFrom'].setValue('');
    this.formData.controls['yearOfConstructionTo'].setValue('');
    this.formData.controls['energyPerformanceLabelId'].setValue('');
    this.formData.controls['primaryUse'].setValue('');
    this.formData.controls['sector'].setValue('');

    this.selectedItemType = null;
  }
  /**
   * Initializes the form data
   * @returns
   */

  protected initFormData(): Promise<any> {
    return new Promise<any>((resolve) => {
      const values = {
        tenant: '',
        surfaceTotalFrom: '',
        surfaceTotalTo: '',
        rentFrom: '',
        rentTo: '',
        municipality: '',
        postcode: '',
        address: '',
        yearOfConstructionFrom: '',
        yearOfConstructionTo: '',
        energyPerformanceLabelId: '',
        primaryUse: '',
        sector: ''
      };
      resolve(values);
    });
  }

  /**
   * Constructor
   * @param eventBus EventBusService
   * @param httpClient HttpClient
   * @param appServiceHelper AppServiceHelperService
   */
  constructor(eventBus: EventBusService, httpClient: HttpClient, appServiceHelper: AppServiceHelperService) {
    super(eventBus, httpClient, 'resetComparablesSearchForm', appServiceHelper);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.requireReflist('ref_lease_transaction_building_energy');
    this.init().then(() => {
      //Nothing
    });
  }

}
